import { apiClient } from '@shared/api/apiClient';
import type { ApiResponse } from '@shared/types/api.types';
import type { FeatureFlagMetadataMap, SystemModuleDto, UpdateModuleRequest } from '../types';

class SystemModuleService {
  private readonly baseUrl = '/system-modules';

  /** Admin surface — requires mod.view_module_list. Used by the Module Management page. */
  async getAll(): Promise<SystemModuleDto[]> {
    const { data } = await apiClient.get<ApiResponse<SystemModuleDto[]>>(this.baseUrl);
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch system modules');
  }

  /**
   * 86d3va61r — "which modules may I use?" for any authenticated user. Same payload as getAll(), but
   * behind no permission, so ModuleGuard actually has data for non-admins. Marked `background` because
   * the app shell fires it on load: a failure must never toast (the store keeps it in availabilityError).
   */
  async getAvailability(): Promise<SystemModuleDto[]> {
    const { data } = await apiClient.get<ApiResponse<SystemModuleDto[]>>(
      `${this.baseUrl}/availability`,
      { background: true }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch module availability');
  }

  async update(id: number, request: UpdateModuleRequest): Promise<SystemModuleDto> {
    const { data } = await apiClient.patch<ApiResponse<SystemModuleDto>>(
      `${this.baseUrl}/${id}`,
      request
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to update system module');
  }

  async getFeatureFlagMetadata(): Promise<FeatureFlagMetadataMap> {
    const { data } = await apiClient.get<ApiResponse<FeatureFlagMetadataMap>>(
      `${this.baseUrl}/feature-flag-metadata`
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch feature flag metadata');
  }
}

export const systemModuleService = new SystemModuleService();
